import React, { useState } from "react";
import {
  Trash2,
  AlertTriangle,
  CheckCircle,
  Mail,
  ShieldOff,
} from "lucide-react";
import Card from "../modern-components/ui/Card";
import Button from "../modern-components/ui/Button";
import Input from "../modern-components/ui/Input";

const consequences = [
  "Your account and profile will be permanently deleted",
  "All your order history and transaction records will be removed",
  "Saved addresses, wishlist, and preferences will be erased",
  "Any pending orders must be fulfilled or cancelled before deletion",
  "Outstanding dues or refunds must be settled before deletion",
  "This action cannot be undone",
];

const DeleteAccount = () => {
  const [step, setStep] = useState(1); // 1: Info, 2: Confirm, 3: Done
  const [formData, setFormData] = useState({
    email: "",
    reason: "",
    confirmation: "",
  });
  const [errors, setErrors] = useState({});

  const reasons = [
    "I no longer need this account",
    "Privacy concerns",
    "Switching to another platform",
    "Too many emails / notifications",
    "Other",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Enter a valid email";
    if (!formData.reason) newErrors.reason = "Please select a reason";
    if (formData.confirmation !== "DELETE")
      newErrors.confirmation = "Type DELETE to confirm";
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // TODO: wire up API call to request account deletion
    setStep(3);
  };

  return (
    <div className="min-h-screen bg-white pt-40 pb-16">
      {/* Hero */}
      <section className="bg-gradient-to-br from-red-600 to-red-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full border border-white/20 mb-6">
            <ShieldOff className="w-4 h-4" />
            <span className="text-sm font-semibold tracking-wide">ACCOUNT</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Delete Account
          </h1>
          <p className="text-lg text-red-100">
            This action is permanent. Please read all information before
            proceeding.
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        {step === 3 ? (
          /* Success State */
          <Card className="text-center py-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Request Submitted
            </h2>
            <p className="text-gray-600 leading-relaxed max-w-md mx-auto">
              Your account deletion request has been received. Our team will
              process it within
              <strong> 7 business days</strong>. You will receive a confirmation
              email at <strong>{formData.email}</strong>.
            </p>
            <p className="text-sm text-gray-500 mt-4">
              If you change your mind, contact us at{" "}
              <a
                href="mailto:support@lohamart.com"
                className="text-primary-600 underline"
              >
                support@lohamart.com
              </a>{" "}
              before processing is complete.
            </p>
          </Card>
        ) : (
          <>
            {/* Warning */}
            <Card className="mb-6 border-l-4 border-red-500 bg-red-50">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-red-800 mb-2">
                    Warning: This action is irreversible
                  </h3>
                  <p className="text-red-700 text-sm leading-relaxed">
                    Deleting your account permanently removes all your data from
                    lohamart. This includes your order history, saved addresses,
                    and preferences. Once deleted, your data cannot be
                    recovered.
                  </p>
                </div>
              </div>
            </Card>

            {/* What happens */}
            <Card className="mb-8 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <Trash2 className="w-5 h-5 text-red-500" />
                <span>What Happens When You Delete Your Account</span>
              </h2>
              <ul className="space-y-3">
                {consequences.map((c, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0 mt-2" />
                    <span className="text-gray-700 text-sm leading-relaxed">
                      {c}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Alternative */}
            <Card className="mb-8 bg-blue-50 border border-blue-100">
              <h3 className="font-bold text-gray-900 mb-2">
                Consider an Alternative
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Instead of deleting your account, you can unsubscribe from
                emails, disable notifications, or simply stop using the
                platform. Your data will remain safe until you decide to return.
                Contact{" "}
                <a
                  href="mailto:support@lohamart.com"
                  className="text-primary-600 underline font-medium"
                >
                  support@lohamart.com
                </a>{" "}
                if you need help managing your preferences.
              </p>
            </Card>

            {/* Form */}
            <Card className="border border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Request Account Deletion
              </h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <Input
                  label="Registered Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={errors.email}
                  placeholder="your@email.com"
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Deletion <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 border rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
                      errors.reason ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="">Select a reason</option>
                    {reasons.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  {errors.reason && (
                    <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type{" "}
                    <span className="font-mono font-bold text-red-600">
                      DELETE
                    </span>{" "}
                    to confirm <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="confirmation"
                    value={formData.confirmation}
                    onChange={handleChange}
                    placeholder="Type DELETE"
                    className={`w-full px-4 py-2.5 border rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all ${
                      errors.confirmation ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.confirmation && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.confirmation}
                    </p>
                  )}
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors duration-200"
                  >
                    <Trash2 className="w-5 h-5" />
                    <span>Submit Deletion Request</span>
                  </button>
                </div>
              </form>
            </Card>

            {/* Support */}
            <div className="mt-6 text-center text-sm text-gray-500">
              Changed your mind?{" "}
              <a
                href="mailto:support@lohamart.com"
                className="text-primary-600 font-medium hover:underline"
              >
                Contact support
              </a>{" "}
              — we're happy to help.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeleteAccount;
